import { useState } from "react";
import { motion } from "framer-motion";
import { Zap, Droplets, Hammer, Snowflake, Sparkles, Scissors, Heart, ArrowUpRight, Star } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { SERVICES, APP_LINKS } from "@/data/site";

const ICONS = { Zap, Droplets, Hammer, Snowflake, Sparkles, Scissors, Heart };

function ServiceCard({ s, i }) {
  const Icon = ICONS[s.icon];
  const [loaded, setLoaded] = useState(false);
  return (
    <motion.a
      href={APP_LINKS.customer}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`service-card-${s.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.08 }}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-azo-border bg-azo-surface shadow-azo transition-all duration-300 hover:-translate-y-1.5 hover:border-azo-primary active:scale-[0.99]"
    >
      {/* image */}
      <div className="relative aspect-[16/11] overflow-hidden bg-azo-surface2">
        <img
          src={s.img}
          alt={`AzoApp ${s.name} service`}
          loading="lazy"
          ref={(n) => { if (n && n.complete) setLoaded(true); }}
          onLoad={() => setLoaded(true)}
          className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-700 ease-out group-hover:scale-105 ${loaded ? "opacity-100" : "opacity-0"}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-transparent" />

        {/* accent icon badge */}
        <span
          className="absolute left-3 top-3 grid h-11 w-11 place-items-center rounded-2xl text-white shadow-lg"
          style={{ backgroundColor: s.accent }}
        >
          <Icon size={20} />
        </span>

        {/* rating chip */}
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xs font-bold text-azo-text shadow">
          <Star size={12} className="fill-azo-amber text-azo-amber" /> {s.rating}
        </span>

        {/* badge */}
        <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1 text-[11px] font-medium text-white border border-white/15">
          <span>{s.emoji}</span> {s.badge}
        </span>
      </div>

      {/* content */}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-heading font-bold text-lg leading-tight">{s.name}</h3>
            <p className="mt-0.5 text-xs text-azo-muted">{s.hindi}</p>
          </div>
        </div>

        <p className="mt-2.5 text-sm text-azo-muted leading-snug">{s.desc}</p>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {s.tags.map((t) => (
            <span key={t} className="rounded-lg bg-azo-surface2 px-2 py-0.5 text-[11px] font-medium text-azo-text/80">
              {t}
            </span>
          ))}
        </div>

        {/* footer */}
        <div className="mt-5 flex items-center justify-between border-t border-azo-border pt-4">
          <div className="leading-none">
            <p className="text-[10px] font-mono2 uppercase tracking-widest text-azo-muted">Starting at</p>
            <p className="mt-1 font-heading font-extrabold text-xl text-azo-primary">{s.from}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-azo-primary px-4 py-2.5 text-sm font-semibold font-heading text-white transition-all duration-300 group-hover:gap-2.5 group-hover:bg-azo-primaryh shadow-azo">
            Book Now <ArrowUpRight size={16} />
          </span>
        </div>
      </div>
    </motion.a>
  );
}

export default function Services() {
  return (
    <section id="services" className="relative py-20 sm:py-28" data-testid="services-bento-grid">
      <div className="pointer-events-none hidden sm:block absolute top-24 -left-40 h-[420px] w-[420px] rounded-full bg-azo-primary/10 blur-[130px]" />
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 sm:mb-12">
          <div>
            <Reveal className="flex items-center gap-2 text-azo-primary font-mono2 text-xs uppercase tracking-widest mb-4">
              <span className="h-px w-8 bg-azo-primary" /> 01 — What we do
            </Reveal>
            <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              <MaskedLines lines={["Saat services,", "ek hi trusted app."]} />
            </h2>
          </div>
          <Reveal delay={0.2} className="max-w-sm text-azo-muted text-base">
            Har category me background-verified, trained pros — taaki har baar mile dependable, bharosemand kaam.
          </Reveal>
        </div>

        {/* app-like quick category chips */}
        <Reveal delay={0.1}>
          <div className="-mx-4 sm:mx-0 mb-8 flex gap-2.5 overflow-x-auto px-4 sm:px-0 pb-2 no-scrollbar">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon];
              return (
                <a
                  key={s.id}
                  href={APP_LINKS.customer}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-chip-${s.id}`}
                  className="group flex shrink-0 items-center gap-2 rounded-full border border-azo-border bg-azo-surface px-4 py-2 text-sm font-medium transition-all duration-300 hover:border-azo-primary hover:bg-azo-primary hover:text-white"
                >
                  <span className="grid h-6 w-6 place-items-center rounded-full text-white" style={{ backgroundColor: s.accent }}>
                    <Icon size={13} />
                  </span>
                  {s.name}
                </a>
              );
            })}
          </div>
        </Reveal>

        {/* premium uniform card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
